import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';

function normalizeDownloadUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('Binary/')) return url;
  const match = url.match(/Binary\/[A-Za-z0-9-]+/);
  return match?.[0] ?? url;
}

/**
 * Resolves Medplum Binary references (e.g. `Binary/{id}`) to a blob URL for `<img>` / Avatar `src`.
 * External http(s) URLs are returned unchanged.
 */
export function useResolvedImageUrl(url: string | undefined): string | undefined {
  const medplum = useMedplum();
  const [resolved, setResolved] = useState<string | undefined>(() =>
    url && /^(https?:|data:|blob:)/i.test(url) ? url : undefined,
  );

  useEffect(() => {
    if (!url) {
      setResolved(undefined);
      return;
    }

    if (/^(https?:|data:|blob:)/i.test(url)) {
      setResolved(url);
      return;
    }

    let blobUrl: string | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const blob = await medplum.download(normalizeDownloadUrl(url));
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setResolved(blobUrl);
      } catch {
        if (!cancelled) setResolved(undefined);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [medplum, url]);

  return resolved;
}
